import React from "react";
import { REGISTER_URL, WORKSHOPS, COHORT_INFO } from "@/lib/site";
import { ArrowUpRight, CalendarDays, Clock, CheckCircle2, Target } from "lucide-react";

export default function Workshops() {
    return (
        <section id="workshops" data-testid="workshops-section" className="py-20 md:py-28 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                    <div>
                        <div className="font-mono-label text-[color:var(--maroon)]">
                            The Workshops · {COHORT_INFO.cohortLabel} · {COHORT_INFO.cohortMonth}
                        </div>
                        <h2
                            className="mt-3 font-serif text-4xl sm:text-5xl text-[color:var(--navy)] tracking-tight leading-tight max-w-3xl"
                            style={{ fontWeight: 500 }}
                        >
                            Five Practical Workshops, One Complete Research Journey.
                        </h2>
                        <div className="hr-gold mt-5" />
                        <p className="mt-5 text-slate-600 max-w-2xl leading-relaxed">
                            Choose one workshop if you need help with a specific stage, or join the
                            full series for complete guidance from research idea to publication.
                        </p>
                    </div>
                    <a
                        href={REGISTER_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="workshops-register-btn"
                        className="btn-outline-navy inline-flex items-center gap-2 self-start rounded-full px-6 py-3 text-sm"
                    >
                        Register Now <ArrowUpRight className="h-4 w-4" />
                    </a>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {WORKSHOPS.map((w, i) => (
                        <article
                            key={w.n}
                            data-testid={`workshop-card-${i + 1}`}
                            className={`group relative rounded-3xl border border-[color:var(--line)] bg-white p-7 sm:p-8 hover:border-[color:var(--maroon)] hover:-translate-y-1 transition-all duration-300 shadow-sm hover:shadow-md ${
                                i === 4 ? "lg:col-span-2" : ""
                            }`}
                        >
                            <div className="flex items-start justify-between gap-6">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3">
                                        <span className="font-serif text-4xl text-[color:var(--maroon)] leading-none">
                                            {w.n}
                                        </span>
                                        <span className="inline-flex items-center rounded-full bg-[color:var(--gold)]/12 text-[color:var(--gold)] font-mono-label px-2.5 py-1">
                                            Workshop {i + 1}
                                        </span>
                                    </div>
                                    <h3 className="mt-4 font-serif text-2xl md:text-[28px] text-[color:var(--navy)] leading-tight" style={{ fontWeight: 500 }}>
                                        {w.title}
                                    </h3>
                                    <p className="mt-1 text-slate-600 italic leading-relaxed">
                                        {w.subtitle}
                                    </p>

                                    <div className="mt-5 flex flex-wrap gap-2">
                                        <span
                                            className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--beige)] px-3 py-1.5 text-xs text-[color:var(--navy)] font-medium"
                                            data-testid={`workshop-${i + 1}-date`}
                                        >
                                            <CalendarDays className="h-3.5 w-3.5 text-[color:var(--maroon)]" />
                                            {w.date}
                                        </span>
                                        <span
                                            className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--beige)] px-3 py-1.5 text-xs text-[color:var(--navy)] font-medium"
                                            data-testid={`workshop-${i + 1}-time`}
                                        >
                                            <Clock className="h-3.5 w-3.5 text-[color:var(--maroon)]" />
                                            {w.time}
                                        </span>
                                    </div>

                                    {/* Outcome */}
                                    <div className="mt-6 rounded-2xl bg-[color:var(--paper-tint)] border border-[color:var(--line)] p-4 flex items-start gap-3">
                                        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white border border-[color:var(--line)] text-[color:var(--maroon)]">
                                            <Target className="h-4 w-4" />
                                        </span>
                                        <div>
                                            <div className="font-mono-label text-[color:var(--maroon)]">
                                                Outcome
                                            </div>
                                            <p
                                                className="mt-1 text-sm text-slate-700 leading-relaxed"
                                                data-testid={`workshop-${i + 1}-outcome`}
                                            >
                                                {w.outcome}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="mt-6">
                                        <div className="font-mono-label text-slate-500 mb-3">
                                            Participants will work on
                                        </div>
                                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                                            {w.focus.map((f) => (
                                                <li
                                                    key={f}
                                                    className="flex items-start gap-2 text-sm text-slate-700"
                                                >
                                                    <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-[color:var(--gold)]" />
                                                    <span>{f}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <div className="mt-6 flex items-center justify-between border-t border-[color:var(--line)] pt-4">
                                        <div className="text-sm flex items-baseline gap-2">
                                            <span className="font-serif text-lg text-[color:var(--navy)]">
                                                RM99
                                            </span>
                                            <span className="text-xs text-slate-500">
                                                per single workshop
                                            </span>
                                        </div>
                                        <a
                                            href={REGISTER_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`workshop-${i + 1}-register-btn`}
                                            className="btn-maroon inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs"
                                        >
                                            Register
                                            <ArrowUpRight className="h-3.5 w-3.5" />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}
